import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import APPOINTMENT_REQUESTS from "../api/services/APPOINTMENT_REQUESTS";
import RequestCard from "../containers/_appointmentRequests/requestCard";
import styles from "../styles/_screens/appointmentRequests.module.scss";

const PENDING_PAGE_SIZE = 20;
const HISTORY_PAGE_SIZE = 50;

const formatDate = (date) => {
  if (!date) return "-";
  try {
    return format(new Date(date), "dd/MM/yyyy");
  } catch {
    return "-";
  }
};

const formatDateTime = (date) => {
  if (!date) return "-";
  try {
    const d = new Date(date);
    return `${format(d, "dd/MM/yyyy")} בשעה ${format(d, "HH:mm")}`;
  } catch {
    return "-";
  }
};

export default function AppointmentRequests() {
  const [activeTab, setActiveTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pendingPage, setPendingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMorePending, setHasMorePending] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [respondingId, setRespondingId] = useState(null);
  const [loadingMorePending, setLoadingMorePending] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);

  // ── Extract items from API response defensively ──
  const extractItems = (data) => {
    if (Array.isArray(data)) return data;
    if (data?.items) return Array.isArray(data.items) ? data.items : [];
    if (data?.data) return Array.isArray(data.data) ? data.data : [];
    if (data?.requests) return Array.isArray(data.requests) ? data.requests : [];
    return [];
  };

  // ── Fetch pending requests ──
  const fetchPending = useCallback(async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingPending(true);
    } else {
      setLoadingMorePending(true);
    }
    try {
      const res = await APPOINTMENT_REQUESTS.getPending(page, PENDING_PAGE_SIZE);
      const data = res?.data;
      const items = extractItems(data);
      const total = data?.total;

      if (append) {
        setPending((prev) => [...prev, ...items]);
      } else {
        setPending(items);
      }

      if (total != null) {
        setHasMorePending(page * PENDING_PAGE_SIZE < total);
      } else {
        setHasMorePending(items.length >= PENDING_PAGE_SIZE);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "שגיאה בטעינת בקשות ממתינות");
      if (!append) setPending([]);
    }
    setLoadingPending(false);
    setLoadingMorePending(false);
  }, []);

  // ── Fetch history ──
  const fetchHistory = useCallback(async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingHistory(true);
    } else {
      setLoadingMoreHistory(true);
    }
    try {
      const res = await APPOINTMENT_REQUESTS.getHistory(page, HISTORY_PAGE_SIZE);
      const data = res?.data;
      const items = extractItems(data);
      const total = data?.total;

      if (append) {
        setHistory((prev) => [...prev, ...items]);
      } else {
        setHistory(items);
      }

      if (total != null) {
        setHasMoreHistory(page * HISTORY_PAGE_SIZE < total);
      } else {
        setHasMoreHistory(items.length >= HISTORY_PAGE_SIZE);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "שגיאה בטעינת היסטוריה");
      if (!append) setHistory([]);
    }
    setLoadingHistory(false);
    setLoadingMoreHistory(false);
    setHistoryFetched(true);
  }, []);

  // ── Fetch pending on mount ──
  useEffect(() => {
    fetchPending(1, false);
  }, [fetchPending]);

  // ── Load more pending ──
  const handleLoadMorePending = () => {
    const nextPage = pendingPage + 1;
    setPendingPage(nextPage);
    fetchPending(nextPage, true);
  };

  // ── Load more history ──
  const handleLoadMoreHistory = () => {
    const nextPage = historyPage + 1;
    setHistoryPage(nextPage);
    fetchHistory(nextPage, true);
  };

  // ── Switch to history tab (lazy load) ──
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === "history" && !historyFetched) {
      fetchHistory(1, false);
    }
  };

  // ── Respond to a request (approve/decline) ──
  const handleRespond = async (id, status) => {
    setRespondingId(id);
    try {
      await APPOINTMENT_REQUESTS.respond(id, status);
      toast.success(status === 1 ? "הבקשה אושרה בהצלחה" : "הבקשה נדחתה");

      // Refetch both lists
      setPendingPage(1);
      setHistoryPage(1);
      await fetchPending(1, false);
      fetchHistory(1, false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "שגיאה בעדכון הבקשה");
    }
    setRespondingId(null);
  };

  // ── Get status info for history rows ──
  const getStatusInfo = (item) => {
    // status could be numeric or string
    const s = item.status;
    if (s === 1 || s === "approved" || s === "APPROVED") {
      return { label: "אושר", className: styles.statusApproved };
    }
    if (s === 2 || s === "declined" || s === "DECLINED" || s === "rejected" || s === "REJECTED") {
      return { label: "נדחה", className: styles.statusDeclined };
    }
    return { label: String(s || "-"), className: "" };
  };

  // ── Initial loading state ──
  if (loadingPending && pending.length === 0) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>
            {/* Header */}
            <div className={styles.header}>
              <h2>בקשות לאישור תורים</h2>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === "pending" ? styles.tabActive : ""}`}
                onClick={() => handleTabChange("pending")}
              >
                ממתינים
                {pending.length > 0 && (
                  <span
                    className={`${styles.badge} ${
                      activeTab === "pending" ? styles.badgeActive : styles.badgeDefault
                    }`}
                  >
                    {pending.length}
                  </span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === "history" ? styles.tabActive : ""}`}
                onClick={() => handleTabChange("history")}
              >
                היסטוריה
              </button>
            </div>

            {/* Content */}
            <div className={styles.contentCard}>
              {/* ── Pending tab ── */}
              {activeTab === "pending" && (
                <>
                  {pending.length === 0 ? (
                    <div className={styles.emptyState}>
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <p>אין בקשות ממתינות</p>
                    </div>
                  ) : (
                    <div className={styles.cardsList}>
                      {pending.map((req) => (
                        <RequestCard
                          key={req._id}
                          request={req}
                          onRespond={handleRespond}
                          respondingId={respondingId}
                        />
                      ))}
                    </div>
                  )}

                  {hasMorePending && pending.length > 0 && (
                    <div className={styles.loadMore}>
                      <button
                        className={styles.loadMoreBtn}
                        onClick={handleLoadMorePending}
                        disabled={loadingMorePending}
                      >
                        {loadingMorePending ? <Spinner /> : "טען עוד"}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ── History tab ── */}
              {activeTab === "history" && (
                <>
                  {loadingHistory && history.length === 0 ? (
                    <div className={styles.emptyState}>
                      <Loader />
                    </div>
                  ) : history.length === 0 ? (
                    <div className={styles.emptyState}>
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <p>אין היסטוריה להצגה</p>
                    </div>
                  ) : (
                    <div className={styles.tableWrapper}>
                      <table>
                        <thead>
                          <tr>
                            <th>לקוח</th>
                            <th>שירות</th>
                            <th>תאריך מבוקש</th>
                            <th>סטטוס</th>
                            <th>תאריך תגובה</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((item) => {
                            const statusInfo = getStatusInfo(item);
                            const customerName =
                              item.customerName || item.customer?.name || "-";
                            const customerPhone =
                              item.customerPhone || item.customer?.phone || "";
                            const serviceName =
                              item.serviceName || item.service?.name || "-";
                            const requestedDate =
                              item.date || item.requestedDate || item.startDate;
                            const responseDate =
                              item.respondedAt || item.updatedAt || item.responseDate;

                            return (
                              <tr key={item._id}>
                                <td>
                                  <div className={styles.customerCell}>
                                    <span className={styles.customerName}>
                                      {customerName}
                                    </span>
                                    {customerPhone && (
                                      <span className={styles.customerPhone}>
                                        {customerPhone}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>{serviceName}</td>
                                <td>{formatDateTime(requestedDate)}</td>
                                <td>
                                  <span
                                    className={`${styles.statusBadge} ${statusInfo.className}`}
                                  >
                                    {statusInfo.label}
                                  </span>
                                </td>
                                <td>{formatDate(responseDate)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {hasMoreHistory && history.length > 0 && (
                    <div className={styles.loadMore}>
                      <button
                        className={styles.loadMoreBtn}
                        onClick={handleLoadMoreHistory}
                        disabled={loadingMoreHistory}
                      >
                        {loadingMoreHistory ? <Spinner /> : "טען עוד"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
